// apps/cli/src/lib/bootstrap-mi.ts
import { runAz } from "./az.js";

/** Description tag on the sub-scope Owner grant — kept in the record even after the MI is deleted, enabling attribution and belt-and-suspenders reap. */
export const INSTALLER_GRANT_DESCRIPTION = "m8t-installer auto-reap (bootstrap)";

export async function ensureResourceGroup(opts: { name: string; location: string; subscriptionId: string }): Promise<void> {
  const existing = (await runAz(["group", "show", "--name", opts.name, "--subscription", opts.subscriptionId, "-o", "json"]).catch(() => "")).trim();
  if (existing) return;
  await runAz([
    "group", "create", "--name", opts.name, "--location", opts.location,
    "--subscription", opts.subscriptionId, "--tags", "m8t-stack=deployment", "--only-show-errors",
  ]);
}

export interface InstallerIdentity { clientId: string; principalId: string; resourceId: string }

export async function createInstallerIdentity(opts: {
  name: string; resourceGroup: string; location: string; subscriptionId: string;
}): Promise<InstallerIdentity> {
  const out = JSON.parse(await runAz([
    "identity", "create", "--name", opts.name, "--resource-group", opts.resourceGroup,
    "--location", opts.location, "--subscription", opts.subscriptionId, "-o", "json",
  ])) as { clientId: string; principalId: string; id: string };
  return { clientId: out.clientId, principalId: out.principalId, resourceId: out.id };
}

/** Owner @ subscription scope — covers resource creation AND role-assignment-write (the gateway's sub-scope reader roles). */
export async function grantOwnerAtSubscription(opts: { principalId: string; subscriptionId: string }): Promise<void> {
  await runAz([
    "role", "assignment", "create",
    "--role", "Owner",
    "--assignee-object-id", opts.principalId,
    "--assignee-principal-type", "ServicePrincipal",
    "--scope", `/subscriptions/${opts.subscriptionId}`,
    "--description", INSTALLER_GRANT_DESCRIPTION,
    "--only-show-errors",
  ]);
}

/**
 * Poll until the freshly-granted installer MI can actually authenticate, BEFORE we
 * kick the ACI. The Owner-at-sub grant can lag the MI token endpoint by minutes on a
 * bad-Azure day (wet-run-2 step-0 death). Moving the wait here surfaces live progress
 * to the founder. Best-effort: on exhaustion we proceed (the installer's own
 * login_with_retry is the backstop) and return false.
 */
export async function waitForMiToken(opts: {
  clientId: string;
  subscriptionId: string;
  attempts?: number;
  sleepMs?: number;
  onProgress?: (m: string) => void;
  azFn?: (args: string[]) => Promise<string>;
}): Promise<boolean> {
  const attempts = opts.attempts ?? 20;
  const sleepMs = opts.sleepMs ?? 9000;
  const az = opts.azFn ?? runAz;
  for (let i = 1; i <= attempts; i++) {
    try {
      // A management-plane read that requires the MI's just-granted role to have
      // resolved. `role assignment list` for the principal is cheap and resolves
      // only once the grant is queryable.
      await az(["role", "assignment", "list", "--assignee", opts.clientId, "--subscription", opts.subscriptionId, "-o", "json"]);
      return true;
    } catch {
      opts.onProgress?.(`waiting for the installer identity to propagate (${String(i)}/${String(attempts)})…`);
      if (sleepMs > 0) await new Promise<void>((r) => setTimeout(r, sleepMs));
    }
  }
  return false;
}

/**
 * Capture the ARM ids of the MI's assignments at launch — reaped by id later
 * (robust vs a gone principal, F3 §7). Filters client-side by `principalId`
 * rather than `--assignee`: a JUST-created MI hasn't propagated to the graph
 * yet, and `--assignee` graph-resolves the principal → "Cannot find ...
 * principal in graph database". The `principalId` field is on every assignment
 * record with no graph lookup, so this works immediately (and also for a
 * later-deleted principal — the same reason §7's teardown filters this way).
 */
export async function listAssignmentIds(opts: { principalId: string; subscriptionId: string }): Promise<string[]> {
  return JSON.parse(await runAz([
    "role", "assignment", "list", "--all",
    "--subscription", opts.subscriptionId,
    "--query", `[?principalId=='${opts.principalId}'].id`, "-o", "json",
  ])) as string[];
}
